import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import SummaryCards from "@/components/listSummary/SummaryCards";
import { useSubscriptionSummary } from "./services/listApi";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { SEARCH_PARAMS, SubscriptionType } from "@/pages/subscriptions/constants";

const isSubscriptionType = (value: string): value is SubscriptionType =>
  Object.values(SubscriptionType).includes(value as SubscriptionType);

export const SubscriptionMetrics = () => {
  const [searchParams] = useSearchParams();
  const { startDate, endDate } = useDateFromSearchParams(
    dayjs().subtract(30, "day").toDate(),
    dayjs().toDate()
  );

  // Build filters from search params
  const filters = useMemo(() => {
    const ruleTypes = searchParams.getAll(SEARCH_PARAMS.RULE_TYPE);
    const categories = searchParams.getAll(SEARCH_PARAMS.CATEGORY);
    const searchValue = searchParams.get(SEARCH_PARAMS.SEARCH);

    return {
      rule_type:
        ruleTypes.length > 0 && isSubscriptionType(ruleTypes[0]) ? ruleTypes[0] : undefined,
      category: categories.length > 0 ? categories[0] : undefined,
      search: searchValue || undefined,
      start_date: startDate?.toISOString(),
      end_date: endDate?.toISOString(),
    };
  }, [searchParams, startDate, endDate]);

  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
  } = useSubscriptionSummary(filters);

  const summaryData = useMemo(() => {
    if (!summary) return [];

    return [
      { key: "total", label: "Total subscriptions", value: summary.total },
      { key: "active", label: "Active", value: summary.active },
      { key: "alerts", label: "Alerts", value: summary.alerts },
      { key: "reports", label: "Reports", value: summary.reports },
    ];
  }, [summary]);

  if (summaryError) {
    return (
      <Stack direction="column" gap="md">
        <Typography variant="body" size="lg">
          Key Metrics
        </Typography>
        <Typography variant="body" className="al-text-red-500 al-text-sm">
          Failed to load metrics
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="column" gap="md" className="al-overflow-hidden al-min-w-0">
      <Typography variant="body" size="lg">
        Key Metrics
      </Typography>
      <SummaryCards isLoading={summaryLoading} summary={summaryData} />
    </Stack>
  );
};
